import EnvironmentConfig from "@/EnvironmentConfig";
import { useAuthStore, useLocaleStore } from "@/store";
import {
  AppConfigurationClient,
  parseFeatureFlag,
} from "@azure/app-configuration";
import { defineStore } from "pinia";
import http from "../http-common";

const providers = {
  azure: "azure", // deprecated
  sanity: "sanity",
};

export const useFeatureFlagStore = defineStore({
  id: "feature-flag",
  state: () => ({
    isInitialized: false,
    featureFlags: [],
    client: null,
  }),
  actions: {
    async initialize() {
      if (this.isInitialized) {
        return;
      }

      // get the feature flag provider (either azure (deprecated) or sanity)
      const flagProvider = EnvironmentConfig.FeatureFlag.Provider;

      // if we are using sanity, then we can't retrieve the feature flags until the user logs in.
      // check with the auth store if there is a valid id, if not return.
      if (flagProvider == providers.sanity) {
        const authStore = useAuthStore();
        if (!authStore.getCurrentUserId) {
          console.log(
            "featureFlagStore: No valid auth. Unable to retreive flags. If signed out this is expected."
          );
          return;
        }
      }

      try {
        // if we are azure, then we need to initialize the client with the connection string
        // we will do this even if we are using the sanity provider (for now)
        if (flagProvider == providers.azure) {
          const connectionString =
            EnvironmentConfig.FeatureFlag.ConnectionString;
          if (connectionString) {
            this.client = new AppConfigurationClient(connectionString);
          }
          if (!this.client) {
            console.error(
              "featureFlagStore: AppConfigurationClient is not initialized "
            );
            this.isInitialized = true; // Allow feature flags to be used but fallback to defaultFlags.
          } else {
            await this.fetchAllFeatureFlags(flagProvider);
            this.isInitialized = true;
          }
        } else {
          await this.fetchAllFeatureFlags(flagProvider);
          this.isInitialized = true;
        }
      } catch (error) {
        console.error(
          "featureFlagStore: Error initialising feature flags",
          error
        );
      }
    },

    async fetchAllFeatureFlags(flagProvider) {
      // if no client, then we can't fetch feature flags
      if (flagProvider == providers.azure && !this.client) {
        return;
      }

      console.log("featureFlagStore: fetchAllFeatureFlags");
      try {
        // Azure App Configuration provider
        if (flagProvider == providers.azure) {
          const clientFeatureFlags =
            await this.client.listConfigurationSettings({
              keyFilter: ".appconfig.featureflag*",
              labelFilter: EnvironmentConfig.FeatureFlag.Environment,
            });

          this.featureFlags = []; // Initialize the array to avoid duplicates
          for await (const featureFlag of clientFeatureFlags) {
            const parsedFeatureFlag = parseFeatureFlag(featureFlag);
            this.featureFlags.push(parsedFeatureFlag);
          }
        } else {
          // Sanity provider
          try {
            // note: flags only come back if any of the environments are enabled.
            const response = await http.get(
              `feature-flags?locale=${useLocaleStore().getCurrentLocaleCode}`
            );

            this.featureFlags = []; // Initialize the array to avoid duplicates

            const flagEnvironment = EnvironmentConfig.FeatureFlag.Environment;
            const clientFeatureFlags = response.data.feature_flags;
            for (let flagName in clientFeatureFlags) {
              let enabledEnvironments = clientFeatureFlags[flagName];
              this.featureFlags.push({
                key: flagName.replaceAll("_", "-"),
                enabled: enabledEnvironments.includes(flagEnvironment),
              });
            }
          } catch (error) {
            console.error("API Error:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching or parsing feature flags:", error);
        throw error;
      }
    },
  },
  getters: {
    isFlagEnabled:
      (state) =>
      (flagKey, defaultFlag = false) => {
        const flagProvider = EnvironmentConfig.FeatureFlag.Provider;
        if (!state.isInitialized) {
          console.error(
            `featureFlagStore: Getting isFlagEnabled(${flagKey}) when store not initialised.`
          );
          return flagProvider == providers.sanity ? false : defaultFlag;
        }

        // Azure App Configuration provider
        if (flagProvider == providers.azure) {
          flagKey = ".appconfig.featureflag/" + flagKey;
          const featureFlag = state.featureFlags.find(
            (flag) => flag.key === flagKey
          );
          if (featureFlag) {
            return featureFlag.value.enabled;
          } else {
            // Fallback to defaultFlag
            console.error(
              `Unable to find feature flag with key: ${flagKey}, defaulting to ${defaultFlag}`
            );
          }
        } else {
          // Sanity provider
          const featureFlag = state.featureFlags.find(
            (flag) => flag.key === flagKey
          );

          if (featureFlag) {
            // console.log(`feature flag ${flagKey} set, returning ${featureFlag.enabled}`);
            return featureFlag.enabled;
          } else {
            // sanity doesn't return the flag if they are not set, so we default to false to remove any confusion
            defaultFlag = false;
            // @todo: remove the following console.log once the MFA feature flags are removed as we don't need to know
            console.log(
              `feature flag ${flagKey} not set, returning ${defaultFlag}`
            );
          }
        }

        return defaultFlag;
      },
  },
});
